import React, { useEffect, useState, useRef } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Handshake, Image, Plus, Trash2, Upload, Save, ExternalLink } from "lucide-react";

const EMPTY = {
  name: "", logo_url: "", website: "", banner_image_url: "",
  display_location: "homepage", starts_at: "", ends_at: "", priority: 0, active: true,
};

export default function AdminSponsors() {
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState(["homepage", "footer", "sponsors_page"]);
  const [editing, setEditing] = useState(null);
  const logoRef = useRef();
  const bannerRef = useRef();

  const load = () => api.get("/admin/sponsors").then(({ data }) => { setItems(data.items || []); setLocations(data.locations || locations); });
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ ...EMPTY });
  const save = async () => {
    if (!editing.name?.trim()) { toast.error("Sponsor name is required"); return; }
    try {
      const payload = { ...editing, priority: Number(editing.priority) || 0 };
      if (editing.id) await api.patch(`/admin/sponsors/${editing.id}`, payload);
      else await api.post("/admin/sponsors", payload);
      toast.success("Sponsor saved"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };
  const toggleActive = async (s) => { await api.patch(`/admin/sponsors/${s.id}`, { active: !s.active }); load(); };
  const del = async (s) => { if (!window.confirm(`Delete sponsor "${s.name}"?`)) return; await api.delete(`/admin/sponsors/${s.id}`); toast.success("Deleted"); load(); };
  const upload = async (file, field) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((e) => ({ ...e, [field]: data.url })); toast.success("Uploaded");
    } catch (e) { toast.error("Upload failed"); }
  };

  return (
    <AdminLayout>
      <div className="p-4 sm:p-8 max-w-4xl">
        <div className="flex items-center gap-2 label-overline text-[#E63946]"><Handshake className="w-4 h-4" /> Sponsors</div>
        <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-tighter mt-2">Sponsor Manager</h1>
        <div className="flex items-center justify-between mt-6 mb-4">
          <p className="text-sm text-slate-500">Partner logos & banners displayed publicly within their active date window.</p>
          <button data-testid="sponsor-new" onClick={startNew} className="inline-flex items-center gap-2 bg-[#E63946] text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm shrink-0"><Plus className="w-4 h-4" /> New</button>
        </div>

        {editing && (
          <div className="bg-white border border-[#0A192F] rounded-sm p-4 mb-6 space-y-3" data-testid="sponsor-editor">
            <input data-testid="sponsor-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Sponsor name" className="w-full px-3 py-2 border border-slate-300 rounded-sm font-display uppercase" />
            <input data-testid="sponsor-website" value={editing.website || ""} onChange={(e) => setEditing({ ...editing, website: e.target.value })} placeholder="Website (https://…)" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="label-overline text-[10px]">Display location</label>
                <select data-testid="sponsor-location" value={editing.display_location} onChange={(e) => setEditing({ ...editing, display_location: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm capitalize">
                  {locations.map((l) => <option key={l} value={l}>{l.replace(/_/g, " ")}</option>)}
                </select>
              </div>
              <div>
                <label className="label-overline text-[10px]">Priority (lower = first)</label>
                <input data-testid="sponsor-priority" type="number" value={editing.priority} onChange={(e) => setEditing({ ...editing, priority: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="label-overline text-[10px]">Active from</label><input type="datetime-local" value={editing.starts_at || ""} onChange={(e) => setEditing({ ...editing, starts_at: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></div>
              <div><label className="label-overline text-[10px]">Active until</label><input type="datetime-local" value={editing.ends_at || ""} onChange={(e) => setEditing({ ...editing, ends_at: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="label-overline text-[10px]">Logo</label>
                {editing.logo_url && <img src={editing.logo_url} alt="" className="w-full h-24 object-contain bg-slate-50 rounded-sm border border-slate-200 mt-1 p-2" />}
                <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0], "logo_url")} />
                <button data-testid="sponsor-logo-upload" onClick={() => logoRef.current.click()} className="mt-2 w-full inline-flex items-center justify-center gap-2 border border-slate-300 px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm"><Upload className="w-3.5 h-3.5" /> Upload Logo</button>
              </div>
              <div>
                <label className="label-overline text-[10px]">Banner (optional)</label>
                {editing.banner_image_url && <img src={editing.banner_image_url} alt="" className="w-full h-24 object-cover rounded-sm border border-slate-200 mt-1" />}
                <input ref={bannerRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0], "banner_image_url")} />
                <button data-testid="sponsor-banner-upload" onClick={() => bannerRef.current.click()} className="mt-2 w-full inline-flex items-center justify-center gap-2 border border-slate-300 px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm"><Upload className="w-3.5 h-3.5" /> Upload Banner</button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 pt-1">
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Active</label>
              <div className="flex-1" />
              <button onClick={() => setEditing(null)} className="text-xs uppercase tracking-wider text-slate-400 px-3">Cancel</button>
              <button data-testid="sponsor-save" onClick={save} className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm"><Save className="w-4 h-4" /> Save</button>
            </div>
          </div>
        )}

        <div className="space-y-2" data-testid="sponsors-list">
          {items.length === 0 && !editing && <div className="bg-white border border-dashed border-slate-300 rounded-sm p-8 text-center text-sm text-slate-500">No sponsors yet.</div>}
          {items.map((s) => (
            <div key={s.id} data-testid={`sponsor-row-${s.id}`} className="bg-white border border-slate-200 rounded-sm p-3 flex items-center gap-3">
              <div className="w-16 h-12 rounded-sm bg-slate-50 overflow-hidden shrink-0 grid place-items-center border border-slate-100">
                {s.logo_url ? <img src={s.logo_url} alt="" className="w-full h-full object-contain p-1" /> : <Image className="w-4 h-4 text-slate-300" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold truncate">{s.name}</div>
                <div className="text-[11px] text-slate-400 flex items-center gap-1 truncate">
                  <span className="capitalize">{(s.display_location || "").replace(/_/g, " ")}</span>
                  {s.website && <>· <a href={s.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 hover:text-[#E63946]">Link <ExternalLink className="w-3 h-3" /></a></>}
                </div>
              </div>
              <button data-testid={`sponsor-toggle-${s.id}`} onClick={() => toggleActive(s)} className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm shrink-0 ${s.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>{s.active ? "Active" : "Inactive"}</button>
              <button data-testid={`sponsor-edit-${s.id}`} onClick={() => setEditing({ ...s, starts_at: s.starts_at || "", ends_at: s.ends_at || "" })} className="text-xs font-semibold uppercase tracking-wider text-[#E63946] shrink-0">Edit</button>
              <button data-testid={`sponsor-delete-${s.id}`} onClick={() => del(s)} className="text-slate-400 hover:text-[#E63946] shrink-0"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
